import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// utils
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { setSession } from './utils';
import { authLogin, authLogout, authMe } from '../api/apiAuth';
// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'DELETE_ACCOUNT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { i18n } = useTranslation();
  const storageAvailable = localStorageAvailable();

  const sanitizeUserForStorage = (user) => {
    if (!user) return null;
    const {
      // Remove abilities and permissions from storage (will be stored separately)
      abilities,
      permissions,
      ...safe
    } = user;
    return safe;
  };

  // const initialize = useCallback(async () => {
  //   try {
  //     const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
  //     const user = storageAvailable ? JSON.parse(localStorage.getItem('user')) : '';

  //     if (accessToken && user) {
  //       setSession(accessToken);

  //       dispatch({
  //         type: 'INITIAL',
  //         payload: {
  //           isAuthenticated: true,
  //           user,
  //         },
  //       });
  //     } else {
  //       dispatch({
  //         type: 'INITIAL',
  //         payload: {
  //           isAuthenticated: false,
  //           user: null,
  //         },
  //       });
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     dispatch({
  //       type: 'INITIAL',
  //       payload: {
  //         isAuthenticated: false,
  //         user: null,
  //       },
  //     });
  //   }
  // }, [storageAvailable]);
  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';

      if (accessToken) {
        setSession(accessToken);

        // استخدام authMe من apiAuth
        const response = await authMe();

        if (response.success && response.data) {
          const userData = response.data;

          // حفظ abilities
          if (storageAvailable) {
            const abilities = userData.abilities || userData.permissions || [];
            const normalizedAbilities = abilities.map(item => (typeof item === 'object' ? item.name : item)).filter(Boolean);
            
            localStorage.setItem('abilities', JSON.stringify(normalizedAbilities));

            // لا نقوم بتغيير اللغة عند كل refresh — نحترم اختيار المستخدم المحلي

            const safeUser = sanitizeUserForStorage(userData);
            localStorage.setItem('user', JSON.stringify(safeUser));
          }

          dispatch({
            type: 'INITIAL',
            payload: {
              isAuthenticated: true,
              user: userData,
            },
          });
          return;
        }
      }

      // في حال عدم وجود توكن أو عدم وجود user صحيح
      setSession(null);
      if (storageAvailable) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('abilities');
      }
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    } catch (error) {
      console.error('🚀 ~ initialize error:', error);

      setSession(null);
      if (storageAvailable) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('abilities');
      }
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // REGISTER
  // const register = useCallback(async (full_name, email, phone_number, password, password_confirmation, role) => {

  //   const response = await axios.post('/signup', { full_name, email, phone_number, password, password_confirmation, role });
  //   console.log("🚀 ~ AuthProvider ~ response:", response)

  //   const { token: access_token, ...user } = response?.data;
  //   setSession(access_token);
  //   localStorage.setItem('user', JSON.stringify(user));
  //   localStorage.setItem('accessToken', access_token);

  //   dispatch({
  //     type: 'REGISTER',
  //     payload: {
  //       user
  //     },
  //   });
  //   return response
  // }, []);


  // const login = useCallback(async (email, password) => {
  //   try {
  //     const payload = {
  //       email,
  //       password,
  //     };
  //     const response = await axios.post('/auth/login', payload);
  //     const { token_type, token, ...user } = response?.data?.data;
  //     setSession(`${token}`);
  //     localStorage.setItem('user', JSON.stringify(user));
  //     localStorage.setItem('accessToken', `${token}`);
  //     dispatch({
  //       type: 'LOGIN',
  //       payload: { user, },
  //     });

  //     return { status: 'success', message: response?.data?.message || 'Login successful', data: user };
  //   } catch (error) {
  //     console.error("🚀 ~ login error:", error);
  //     return {
  //       status: 'error',
  //       message: error?.response?.data?.message || 'البريد الألكتروني او كلمة المرور غير صحيحة',
  //     };
  //   }
  // }, []);

  const login = useCallback(async (email, password) => {
    try {
      // استخدام authLogin من apiAuth
      const response = await authLogin({ email, password });

      if (response.success && response.data) {
        const { token, data: userData } = response;

        // حفظ Token
        setSession(token);
        if (storageAvailable) {
          localStorage.setItem('accessToken', token);

          // حفظ abilities
          const abilities = userData.abilities || userData.permissions || [];
          const normalizedAbilities = abilities.map(item => (typeof item === 'object' ? item.name : item)).filter(Boolean);
          
          localStorage.setItem('abilities', JSON.stringify(normalizedAbilities));

          // اللغة: نطبق lang القادم من السيرفر فقط إذا لم يختر المستخدم لغة محلياً
          if (userData.lang && !localStorage.getItem('i18nextLng')) {
            i18n.changeLanguage(userData.lang);
          }

          const safeUser = sanitizeUserForStorage(userData);
          localStorage.setItem('user', JSON.stringify(safeUser));
        }

        dispatch({
          type: 'LOGIN',
          payload: { user: userData },
        });

        return {
          status: 'success',
          message: response.message || 'Login successful',
          data: userData,
        };
      } else {
        return {
          status: 'error',
          message: response.message || 'Login failed',
        };
      }
    } catch (error) {
      console.error('🚀 ~ login error:', error);
      return {
        status: 'error',
        message:
          error?.response?.data?.message ||
          'البريد الألكتروني او كلمة المرور غير صحيحة',
      };
    }
  }, [storageAvailable, i18n]);


  // LOGOUT
  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch (error) {
      console.error('🚀 ~ logout error:', error);
    } finally {
      setSession(null);
      if (storageAvailable) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('abilities');
      }
      dispatch({
        type: 'LOGOUT',
      });
    }
  }, [storageAvailable]);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      // register,
      login,
      logout
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
